import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Navbar from '../components/Navbar';

const DEFAULT_VIEW = [39.5, -98.35, 4];
const DEFAULT_RADIUS = 500;

/**
 * Reads the pair of decimal-degree coordinates a cell-ping report gives —
 * tolerant of the formatting variance those actually come in: comma and/or
 * whitespace between the two numbers, an optional leading/trailing N/S/E/W
 * letter (which also fixes the sign), and an optional degree symbol.
 *
 * @returns {{lat: number, lng: number} | null}
 */
export function parseCoordinates(input) {
  if (!input) return null;
  const cleaned = input
    .replace(/\u00b0/g, '')
    // Re-join a direction letter onto its number whether the letter came
    // before ("N 40.71") or after ("40.71 N") a space, so a degree symbol
    // that left a gap behind ("40.71\u00b0 N") doesn't get mistaken for a
    // third token. The negative lookahead keeps this from also swallowing a
    // SECOND number's own leading letter (e.g. the "W" in "40.71 W74.00"
    // belongs to the next token, not a trailing suffix on this one).
    .replace(/([NSEW])\s+(-?\d)/gi, '$1$2')
    .replace(/(-?\d)\s+([NSEW])(?!\d)/gi, '$1$2');

  const parts = cleaned.trim().split(/[,\s]+/).filter(Boolean);
  if (parts.length !== 2) return null;

  const parseOne = (raw) => {
    const m = /^([NSEW])?\s*(-?\d+(?:\.\d+)?)\s*°?\s*([NSEW])?$/i.exec(raw);
    if (!m) return null;
    const value = parseFloat(m[2]);
    if (!isFinite(value)) return null;
    const dir = (m[1] || m[3] || '').toUpperCase();
    if (dir === 'S' || dir === 'W') return -Math.abs(value);
    if (dir === 'N' || dir === 'E') return Math.abs(value);
    return value;
  };

  const lat = parseOne(parts[0]);
  const lng = parseOne(parts[1]);
  if (lat == null || lng == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  return { lat, lng };
}

function fmtMeters(n) {
  return n >= 1000 ? `${(n / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} km` : `${n.toLocaleString()} m`;
}

function PingMap({ center, radius }) {
  const host = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const circle = useRef(null);

  // Map is created once and never torn down until the component unmounts —
  // re-centering/redrawing on new input happens in the effect below instead.
  useEffect(() => {
    if (!host.current || map.current) return undefined;
    const m = L.map(host.current, { zoomControl: true }).setView(DEFAULT_VIEW.slice(0, 2), DEFAULT_VIEW[2]);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(m);
    map.current = m;
    return () => { m.remove(); map.current = null; };
  }, []);

  useEffect(() => {
    if (!map.current) return;
    if (marker.current) { marker.current.remove(); marker.current = null; }
    if (circle.current) { circle.current.remove(); circle.current = null; }
    if (!center || !radius) return;

    const latlng = [center.lat, center.lng];
    // A drawn dot, not Leaflet's default pin icon — that icon's image assets
    // don't resolve correctly once webpack bundles leaflet.css, and show up
    // as broken-image placeholders. Same fix MapSearch.js already uses.
    marker.current = L.circleMarker(latlng, {
      radius: 7, color: '#fff', weight: 2, fillColor: '#4f46e5', fillOpacity: 1,
    }).addTo(map.current);
    circle.current = L.circle(latlng, {
      radius, color: '#4f46e5', weight: 2, fillColor: '#818cf8', fillOpacity: 0.15,
    }).addTo(map.current);

    map.current.fitBounds(circle.current.getBounds(), { padding: [24, 24], maxZoom: 17 });
  }, [center, radius]);

  return <div ref={host} style={{ height: 480, width: '100%', borderRadius: 10, overflow: 'hidden', border: '1px solid #cbd5e1' }} />;
}

const fieldLabelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 };
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13, color: '#0f172a',
  border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', fontFamily: 'inherit',
};

export default function PingMapper() {
  const [raw, setRaw] = useState('');
  const [radiusInput, setRadiusInput] = useState(String(DEFAULT_RADIUS));

  const trimmed = raw.trim();
  const parsed = parseCoordinates(trimmed);
  const radius = Number(radiusInput);
  const validRadius = radius > 0 && isFinite(radius);
  const showError = trimmed.length > 0 && !parsed;
  const ready = parsed && validRadius;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: '#0f172a',
    }}>
      <Navbar pageTitle="Ping Mapper" />

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 72px' }}>
        <div style={{
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
          padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ flex: '2 1 320px', minWidth: 240 }}>
              <label style={fieldLabelStyle}>Ping coordinates</label>
              <input
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder='e.g. 40.7128, -74.0060'
                style={inputStyle}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Latitude, longitude — comma or space separated. N/S/E/W letters are also read.
              </div>
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 140 }}>
              <label style={fieldLabelStyle}>Radius (meters)</label>
              <input
                type="number"
                min="1"
                value={radiusInput}
                onChange={(e) => setRadiusInput(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {showError && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: '#b91c1c', lineHeight: 1.6 }}>
              Could not read a coordinate pair from that text — expected something like
              &quot;40.7128, -74.0060&quot; or &quot;40.7128N, 74.0060W&quot;.
            </div>
          )}
          {trimmed && parsed && !validRadius && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: '#b91c1c', lineHeight: 1.6 }}>
              Enter a radius greater than 0.
            </div>
          )}
        </div>

        {ready && (
          <>
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14,
              background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
              padding: '12px 18px', marginBottom: 16, fontSize: 13,
            }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                {parsed.lat.toFixed(6)}, {parsed.lng.toFixed(6)}
              </span>
              <span style={{ color: '#64748b' }}>{fmtMeters(radius)} radius</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, fontSize: 12.5, fontWeight: 600 }}>
                <a
                  href={`https://www.google.com/maps?q=${parsed.lat},${parsed.lng}`}
                  target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}
                >
                  Google Maps ↗
                </a>
                <a
                  href={`https://www.openstreetmap.org/?mlat=${parsed.lat}&mlon=${parsed.lng}#map=16/${parsed.lat}/${parsed.lng}`}
                  target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}
                >
                  OpenStreetMap ↗
                </a>
              </div>
            </div>

            <PingMap center={parsed} radius={radius} />

            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>
              The shaded circle is the reported ping radius, not a precise location — the device
              could be anywhere inside it.
            </div>
          </>
        )}

        {!raw && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, marginTop: 40, lineHeight: 1.7 }}>
            Paste the GPS coordinate and radius from a cell-ping report to see the search area.
          </div>
        )}
      </div>
    </div>
  );
}
